import { useEffect, useState } from 'react';
import type { FutebolLivedataPitch } from '@/shared/types';
import {
  corDestaquePitch,
  isSituacaoAltaPressao,
  labelSituacaoPitch,
  rastroCoordenadas,
  urlArenaPitch2d,
} from '../../domain/usecases/pitchSituacao';
import { useFutebolWssBroadcast } from '../hooks/useFutebolWssBroadcast';

const TICK_MS = 3_000;
const ARENA_KEY = (import.meta.env.VITE_BSD_ARENA_EMBED_KEY as string | undefined)?.trim() ?? '';

interface Props {
  fixtureId:     number;
  fallbackPitch: FutebolLivedataPitch | null;
}

/**
 * Mini-campo ao vivo. Com `VITE_BSD_ARENA_EMBED_KEY`, usa o Arena 2D oficial
 * (`dim=2`). Sem a chave, desenha o campo 2D nosso a partir do relay WSS.
 */
export default function TvLivePitch({ fixtureId, fallbackPitch }: Props) {
  if (ARENA_KEY) {
    return <ArenaPitch2d fixtureId={fixtureId} />;
  }
  return <PitchLocal fixtureId={fixtureId} fallbackPitch={fallbackPitch} />;
}

function ArenaPitch2d({ fixtureId }: { fixtureId: number }) {
  const src = urlArenaPitch2d(fixtureId, ARENA_KEY, true);

  return (
    <div
      className="w-[clamp(280px,32vw,520px)] pointer-events-none select-none"
      aria-hidden="true"
    >
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16 / 10' }}>
        <iframe
          title="Live pitch"
          src={src}
          className="absolute inset-0 h-full w-full border-0"
          loading="eager"
          referrerPolicy="origin"
        />
      </div>
    </div>
  );
}

function PitchLocal({ fixtureId, fallbackPitch }: Props) {
  const { pitch, bridgeAtivo, canalConectado } = useFutebolWssBroadcast(fixtureId, fallbackPitch);
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const pitchAtivo = pitch ?? fallbackPitch;
  const aguardando = !pitchAtivo;
  const fresco     = pitchAtivo ? Date.now() - pitchAtivo.uts < 20_000 : false;
  const rastro     = rastroCoordenadas(pitchAtivo?.coordinates ?? null);
  const bola       = rastro.length > 0 ? rastro[rastro.length - 1] : null;
  const destaque   = corDestaquePitch(pitchAtivo?.situation);
  const label      = pitchAtivo
    ? labelSituacaoPitch(pitchAtivo.situation)
    : statusAguardando(canalConectado, bridgeAtivo);
  const pressao       = pitchAtivo ? isSituacaoAltaPressao(pitchAtivo.situation) : false;
  const ladoCasa      = pitchAtivo?.side === 'home';
  const ladoVisitante = pitchAtivo?.side === 'away';

  return (
    <div
      className="w-[clamp(220px,24vw,380px)] pointer-events-none select-none"
      aria-hidden="true"
    >
      <div
        className="overflow-visible bg-transparent"
        style={{
          opacity:    aguardando ? 0.85 : fresco ? 1 : 0.55,
          transition: 'opacity 0.6s ease',
        }}
      >
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <span className="text-[clamp(11px,0.75vw,14px)] font-bold text-white tracking-tight">
              Live pitch
            </span>
            {bridgeAtivo && fresco && (
              <span className="text-[8px] font-bold uppercase tracking-wider text-emerald-400/90 px-1.5 py-0.5 rounded bg-emerald-500/15">
                Ao vivo
              </span>
            )}
          </div>
          <span className="text-[clamp(9px,0.55vw,11px)] text-white/40 font-medium truncate max-w-[50%] text-right">
            {aguardando ? label : ladoVisitante ? 'Visitante' : ladoCasa ? 'Casa' : ''}
          </span>
        </div>

        <div
          className="relative px-3 pt-3 pb-1"
          style={{ perspective: '820px', perspectiveOrigin: '50% 110%' }}
        >
          <div
            className="relative w-full"
            style={{
              aspectRatio: '100 / 68',
              transform: 'rotateX(54deg) rotateZ(-9deg)',
              transformOrigin: '50% 55%',
              transformStyle: 'preserve-3d',
            }}
          >
            <div
              className="absolute inset-0 overflow-hidden"
              style={{
                borderRadius: '3px',
                background: [
                  'repeating-linear-gradient(90deg, #1b6b3c 0 10%, #175c34 10% 20%)',
                  'linear-gradient(180deg, rgba(0,0,0,0.18), transparent 18%, transparent 82%, rgba(0,0,0,0.28))',
                ].join(', '),
                boxShadow: '0 18px 28px rgba(0,0,0,0.45), inset 0 0 0 2px rgba(255,255,255,0.22)',
              }}
            />

            {pressao && ladoCasa && (
              <div
                className="absolute inset-y-0 left-0 w-[48%]"
                style={{ background: 'linear-gradient(90deg, rgba(74,222,128,0.38), transparent)' }}
              />
            )}
            {pressao && ladoVisitante && (
              <div
                className="absolute inset-y-0 right-0 w-[48%]"
                style={{ background: 'linear-gradient(270deg, rgba(251,191,36,0.32), transparent)' }}
              />
            )}

            <svg
              viewBox="0 0 100 62"
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <rect x="1.2" y="1.2" width="97.6" height="59.6" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.55" />
              <line x1="50" y1="1.2" x2="50" y2="60.8" stroke="rgba(255,255,255,0.45)" strokeWidth="0.4" />
              <circle cx="50" cy="31" r="8" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.4" />
              <circle cx="50" cy="31" r="0.7" fill="rgba(255,255,255,0.55)" />
              <rect x="1.2" y="13" width="16.5" height="36" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.4" />
              <rect x="82.3" y="13" width="16.5" height="36" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.4" />
              <rect x="1.2" y="21.5" width="6.5" height="19" fill="none" stroke="rgba(255,255,255,0.32)" strokeWidth="0.35" />
              <rect x="92.3" y="21.5" width="6.5" height="19" fill="none" stroke="rgba(255,255,255,0.32)" strokeWidth="0.35" />
              <path d="M17.7 24.5 A8 8 0 0 1 17.7 37.5" fill="none" stroke="rgba(255,255,255,0.32)" strokeWidth="0.35" />
              <path d="M82.3 24.5 A8 8 0 0 0 82.3 37.5" fill="none" stroke="rgba(255,255,255,0.32)" strokeWidth="0.35" />
            </svg>

            {rastro.map((c, i) => {
              const t = (i + 1) / rastro.length;
              const size = 3 + t * 5;
              return (
                <div
                  key={`${c.x}-${c.y}-${i}`}
                  className="absolute rounded-full"
                  style={{
                    width:  size,
                    height: size,
                    left:   `${c.x}%`,
                    top:    `${c.y}%`,
                    transform: 'translate(-50%, -50%)',
                    background: destaque,
                    opacity: 0.12 + t * 0.45,
                    boxShadow: t > 0.7 ? `0 0 ${6 + t * 8}px ${destaque}` : 'none',
                  }}
                />
              );
            })}

            {bola && (
              <div
                className="absolute rounded-full"
                style={{
                  width:  'clamp(9px, 0.75vw, 13px)',
                  height: 'clamp(9px, 0.75vw, 13px)',
                  left:   `${bola.x}%`,
                  top:    `${bola.y}%`,
                  transform: 'translate(-50%, -50%)',
                  background: `radial-gradient(circle at 35% 30%, #fff, ${destaque} 55%, ${destaque})`,
                  boxShadow: `0 0 12px ${destaque}, 0 0 22px ${destaque}88`,
                  border: '1px solid rgba(255,255,255,0.85)',
                  transition: fresco ? 'left 0.28s linear, top 0.28s linear' : 'none',
                }}
              />
            )}

            {aguardando && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 px-3">
                <span className="text-[clamp(9px,0.6vw,11px)] text-white/55 font-medium text-center">
                  {canalConectado ? 'aguardando rastreio…' : 'conectando…'}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 px-3 pb-2.5 pt-1">
          {pressao && (
            <span
              className="inline-block w-1.5 h-1.5 rounded-full animate-pulse shrink-0"
              style={{ background: destaque }}
            />
          )}
          <span
            className="text-[clamp(10px,0.7vw,13px)] font-semibold truncate"
            style={{
              color: aguardando
                ? 'rgba(255,255,255,0.45)'
                : pressao
                  ? destaque
                  : 'rgba(255,255,255,0.78)',
            }}
          >
            {label}
          </span>
        </div>
      </div>
    </div>
  );
}

function statusAguardando(canalConectado: boolean, bridgeAtivo: boolean): string {
  if (bridgeAtivo) return 'Ao vivo';
  if (canalConectado) return 'Aguardando rastreio';
  return 'Conectando…';
}
